import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, WS_API } from "@/lib/apiClient";
import { formatIST, displayDoctorName } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { IndiaMosaicMap } from "@/components/IndiaMosaicMap";
import { CountUp } from "@/components/CountUp";
import { Button } from "@/components/ui/button";
import {
  Maximize2, Minimize2, Users, MapPin, Landmark,
  Share2, Search, Target,
} from "lucide-react";
import Marquee from "react-fast-marquee";
import confetti from "canvas-confetti";
import { Input } from "@/components/ui/input";
import { CAMPAIGN, ORG, PLEDGE_TARGET, BRAND_LOGOS } from "@/lib/constants";

// New pledges arrive over the websocket instantly; polling is only a
// fallback for missed events (reconnects, admin moderation changes).
const POLL_MS = 30000;
const SPOTLIGHT_MS = 35000; // client ask: new entries stay on screen 30-45s
const SEARCH_POPUP_MS = 30000; // client ask: searched doctor popup auto-closes

const TRICOLOR = ["#FF9933", "#FFFFFF", "#138808", "#06038D"];

// Record finale: 10..0, 5s per number, entries close at 0 (must match backend).
const FINALE_STEP_MS = 5000;
const FINALE_TOTAL_SECONDS = 55;

// Promo-link pledges may have no specialty/city — join only what exists.
const joinMeta = (...parts) => parts.filter(Boolean).join(" | ");

// Fireworks volley sized by milestone: 1 = every 100, 2 = every 500, 3 = every
// 1000 (and the 10k goal). Bursts fire from the bottom corners and centre so
// they read well on a large LED wall.
function fireworks(power) {
  const duration = power === 3 ? 9000 : power === 2 ? 5000 : 1800;
  const end = Date.now() + duration;
  const frame = () => {
    confetti({
      particleCount: 30 * power,
      startVelocity: 40 + 12 * power,
      spread: 70 + 25 * power,
      ticks: 180,
      colors: TRICOLOR,
      origin: { x: Math.random() < 0.5 ? 0.1 + Math.random() * 0.2 : 0.7 + Math.random() * 0.2, y: 0.9 },
      zIndex: 80,
    });
    if (power >= 2) {
      confetti({
        particleCount: 20 * power,
        startVelocity: 55,
        spread: 120,
        ticks: 200,
        colors: TRICOLOR,
        origin: { x: 0.5, y: 0.6 },
        zIndex: 80,
      });
    }
    if (Date.now() < end) setTimeout(frame, power === 1 ? 500 : 350);
  };
  frame();
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div data-testid="wall-stat-tile" className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 shadow-sm">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#0056D2]/10 text-[#0056D2] shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </span>
      <div className="min-w-0">
        <p className="font-display font-extrabold text-xl leading-none text-slate-900">
          <CountUp value={value ?? 0} />
        </p>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function PledgeAvatar({ pledge, size = "w-10 h-10", text = "text-xs" }) {
  const initials = (pledge.name || "").replace(/^Dr\.?\s*/i, "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return pledge.photo ? (
    <img src={pledge.photo} alt={pledge.name} className={`${size} rounded-full object-cover shrink-0 ring-2 ring-[#FF9933]/40`} />
  ) : (
    <span className={`grid place-items-center ${size} rounded-full bg-gradient-to-br from-[#0056D2] to-[#138808] text-white ${text} font-display font-bold shrink-0`}>
      {initials || "DR"}
    </span>
  );
}

function RecentPledgeRow({ pledge, onSelect, noDr = false }) {
  const name = noDr ? pledge.name : displayDoctorName(pledge.name);
  return (
    <button
      type="button"
      onClick={() => onSelect?.(pledge)}
      data-testid="recent-pledge-row"
      className="w-full text-left flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-3 shadow-sm hover:border-[#0056D2]/50 hover:shadow transition"
    >
      <PledgeAvatar pledge={pledge} />
      <div className="min-w-0">
        <p className="font-display font-bold text-sm text-slate-900 truncate">{name}</p>
        <p className="text-xs text-slate-500 truncate">{joinMeta(pledge.specialty, pledge.city)}</p>
      </div>
    </button>
  );
}

export default function LiveWallPage({ team = false }) {
  const [stats, setStats] = useState(null);
  const [pledges, setPledges] = useState([]);
  // Map tiles show only doctors with a photo; the recent list shows everyone.
  const [mapPledges, setMapPledges] = useState([]);
  const [fs, setFs] = useState(false);
  const [selected, setSelected] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const containerRef = useRef(null);
  const spotlightTimer = useRef(null);
  const popupTimer = useRef(null);

  // ----- Record finale (Complete Record) -----
  const [finaleStart, setFinaleStart] = useState(null); // ms epoch (local clock)
  const [finaleNum, setFinaleNum] = useState(null);
  const [finalInfo, setFinalInfo] = useState(null); // {count}

  // Resume state on load (refresh-proof, uses server clock offset).
  useEffect(() => {
    if (team) return;
    api.get("/campaign-state").then((r) => {
      const s = r.data;
      if (s.record_completed) {
        setFinalInfo({ count: s.final_count });
      } else if (s.finale_started_at) {
        const offset = Date.now() - new Date(s.server_now).getTime();
        setFinaleStart(new Date(s.finale_started_at).getTime() + offset);
      }
    }).catch(() => {});
  }, [team]);

  // Drive the countdown: number switches every 5s with fireworks, then the
  // final achieved count takes over.
  useEffect(() => {
    if (finaleStart == null) return;
    let lastNum = null;
    const tick = () => {
      const elapsed = (Date.now() - finaleStart) / 1000;
      if (elapsed >= FINALE_TOTAL_SECONDS) {
        api.get("/campaign-state").then((r) => {
          setFinalInfo({ count: r.data.final_count ?? stats?.total_pledges ?? 0 });
        }).catch(() => setFinalInfo({ count: stats?.total_pledges ?? 0 }));
        setFinaleStart(null);
        fireworks(3);
        return;
      }
      const num = Math.max(0, 10 - Math.floor((elapsed * 1000) / FINALE_STEP_MS));
      if (num !== lastNum) {
        lastNum = num;
        setFinaleNum(num);
        fireworks(num === 0 ? 3 : num <= 3 ? 2 : 1);
      }
    };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finaleStart]);

  // Milestone celebrations: fireworks + banner when the live count crosses
  // every 100 (small), 500 (big) or 1000 / the goal (mega).
  const prevTotalRef = useRef(null);
  const [milestone, setMilestone] = useState(null);
  const milestoneTimer = useRef(null);
  useEffect(() => {
    if (team) return; // no record milestones on the team wall
    const total = stats?.total_pledges;
    if (total == null) return;
    const prev = prevTotalRef.current;
    prevTotalRef.current = total;
    if (prev == null || total <= prev) return; // first load / no growth
    const crossed = (step) => Math.floor(total / step) > Math.floor(prev / step);
    const showBanner = (value, label) => {
      setMilestone({ value, label });
      clearTimeout(milestoneTimer.current);
      milestoneTimer.current = setTimeout(() => setMilestone(null), 10000);
    };
    if (prev < PLEDGE_TARGET && total >= PLEDGE_TARGET) {
      fireworks(3);
      showBanner(PLEDGE_TARGET, "GOAL ACHIEVED!");
    } else if (crossed(1000)) {
      fireworks(3);
      showBanner(Math.floor(total / 1000) * 1000, "PLEDGES!");
    } else if (crossed(500)) {
      fireworks(2);
      showBanner(Math.floor(total / 500) * 500, "PLEDGES!");
    } else if (crossed(100)) {
      fireworks(1);
    }
  }, [stats?.total_pledges]);
  useEffect(() => () => clearTimeout(milestoneTimer.current), []);

  // Hidden test trigger for the LED screen: /wall?celebrate=5000 (or any
  // number) plays that milestone's celebration once, then strips the param.
  useEffect(() => {
    if (team) return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("celebrate");
    if (raw == null) return;
    const value = parseInt(raw, 10) || 5000;
    const power = value >= PLEDGE_TARGET || value % 1000 === 0 ? 3 : value % 500 === 0 ? 2 : 1;
    fireworks(power);
    if (power >= 2 || value >= PLEDGE_TARGET) {
      setMilestone({ value, label: value >= PLEDGE_TARGET ? "GOAL ACHIEVED!" : "PLEDGES!" });
      clearTimeout(milestoneTimer.current);
      milestoneTimer.current = setTimeout(() => setMilestone(null), 10000);
    }
    params.delete("celebrate");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, []);

  const load = () => {
    api.get(`/stats${team ? "?team=true" : ""}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/pledges/recent?limit=120${team ? "&team=true" : ""}`).then((r) => setPledges(r.data)).catch(() => {});
    api.get(`/pledges/recent?limit=200&with_photo=true${team ? "&team=true" : ""}`).then((r) => setMapPledges(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // Live updates: prepend pledges pushed over the websocket, refresh stats.
  useEffect(() => {
    let ws;
    let retry;
    let closed = false;
    const connect = () => {
      ws = new WebSocket(`${WS_API}/ws/wall`);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (!team && msg.type === "finale") {
            setFinalInfo(null);
            setFinaleStart(Date.now());
          }
          if (!team && msg.type === "finale_cancelled") {
            setFinaleStart(null);
            setFinaleNum(null);
            setFinalInfo(null);
          }
          if (msg.type === (team ? "team_pledge" : "pledge") && msg.pledge) {
            // Show the arrival card first; it lands on the map when the
            // animation finishes and the pledge is prepended to the list.
            setIncoming(msg.pledge);
            setTimeout(() => {
              setPledges((prev) => [msg.pledge, ...prev.filter((p) => p.id !== msg.pledge.id)].slice(0, 200));
              if (msg.pledge.photo) {
                setMapPledges((prev) => [msg.pledge, ...prev.filter((p) => p.id !== msg.pledge.id)].slice(0, 200));
              }
              setIncoming(null);
              // keep the newest entry spotlighted beside the map for a while
              setSpotlight(msg.pledge);
              clearTimeout(spotlightTimer.current);
              spotlightTimer.current = setTimeout(() => setSpotlight(null), SPOTLIGHT_MS);
            }, 1600);
            api.get(`/stats${team ? "?team=true" : ""}`).then((r) => setStats(r.data)).catch(() => {});
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onclose = () => {
        if (!closed) retry = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      ws?.close();
    };
  }, []);

  useEffect(() => {
    const handler = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFs = () => {
    if (!document.fullscreenElement) containerRef.current?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // Doctor search (debounced). Picking a result opens the record popup, which
  // auto-closes after SEARCH_POPUP_MS.
  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/pledges/search", { params: { q: query.trim(), ...(team ? { team: true } : {}) } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const openPopup = (pledge, { autoClose = false } = {}) => {
    setSelected(pledge);
    clearTimeout(popupTimer.current);
    if (autoClose) popupTimer.current = setTimeout(() => setSelected(null), SEARCH_POPUP_MS);
  };

  const closePopup = () => {
    clearTimeout(popupTimer.current);
    setSelected(null);
  };

  useEffect(() => () => { clearTimeout(popupTimer.current); clearTimeout(spotlightTimer.current); }, []);

  const shareText = encodeURIComponent(`${CAMPAIGN.title} — ${CAMPAIGN.tagline} #HealthyIndiaPledge`);
  const shareUrl = encodeURIComponent(typeof window !== "undefined" ? window.location.origin : "");

  return (
    <div ref={containerRef} data-testid="live-wall" className="min-h-screen lg:h-screen bg-[#faf6ee] text-slate-900 flex flex-col overflow-y-auto overflow-x-hidden lg:overflow-hidden">
      <div className="tricolor-bar h-1 w-full shrink-0" aria-hidden="true" />

      {/* Top panel per client design: navy band, title left, target counter
          centre, Blue Cross + ABR logos, campaign dates, Exit button */}
      <header className="shrink-0 px-4 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 bg-[#1F2B7B] text-white shadow-md">
        {/* Blue Cross logo leads the header on every screen size */}
        <div className="flex items-center gap-3 lg:gap-4 min-w-0">
          <span className="grid place-items-center w-12 h-12 lg:w-16 lg:h-16 rounded-xl bg-white p-1 lg:p-1.5 shrink-0">
            <img src={ORG.logo} alt={`${ORG.name} logo`} className="w-full h-full" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-lg sm:text-xl lg:text-2xl leading-none uppercase tracking-wide">{CAMPAIGN.title}</h1>
            <p className="text-[10px] lg:text-[11px] text-blue-100 tracking-wide mt-1">Thousands of Doctors. One Commitment. A Healthier India.</p>
          </div>
        </div>

        {/* Countdown against the campaign target */}
        {!team && <div data-testid="target-countdown" className="order-last lg:order-none w-full lg:w-auto lg:flex-1 lg:max-w-md">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 font-bold">
              <Target className="w-3.5 h-3.5 text-[#FF9933]" />
              <motion.span
                key={stats?.total_pledges ?? 0}
                initial={{ scale: 1.45 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 14 }}
                className="inline-block origin-left"
              >
                <CountUp value={stats?.total_pledges ?? 0} className="font-display text-lg text-white" />
              </motion.span>
              <span className="text-blue-200 font-medium">/ {PLEDGE_TARGET.toLocaleString("en-IN")} pledges</span>
            </span>
            {(stats?.total_pledges ?? 0) >= PLEDGE_TARGET ? (
              <span data-testid="goal-achieved" className="font-display font-extrabold text-[#FFD700]">
                🎉 Goal achieved · +{((stats?.total_pledges ?? 0) - PLEDGE_TARGET).toLocaleString("en-IN")} beyond!
              </span>
            ) : (
              <span className="font-display font-extrabold text-[#7CFC9B]">
                {(PLEDGE_TARGET - (stats?.total_pledges ?? 0)).toLocaleString("en-IN")} to go
              </span>
            )}
          </div>
          <div className="h-2 rounded-full bg-white/20 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#FF9933] via-white to-[#7CFC9B] transition-all duration-1000"
              style={{ width: `${Math.min(100, ((stats?.total_pledges ?? 0) / PLEDGE_TARGET) * 100)}%` }}
            />
          </div>
        </div>}

        <div className="flex items-center gap-3 lg:gap-5 shrink-0">
          {/* Asia Book of Records emblem (styled placeholder until final asset) */}
          <span data-testid="abr-logo" className="flex flex-col items-center justify-center w-20 lg:w-22 h-11 lg:h-12 rounded-md bg-[#F58220] border-2 border-white text-center leading-none px-1">
            <span className="font-display font-black text-white text-sm tracking-tight" style={{ textShadow: "1px 1px 0 #C1272D" }}>ASIA</span>
            <span className="font-display font-black text-white text-[9px] tracking-tight" style={{ textShadow: "1px 1px 0 #C1272D" }}>BOOK OF</span>
            <span className="font-display font-black text-white text-[11px] tracking-tight" style={{ textShadow: "1px 1px 0 #C1272D" }}>RECORDS</span>
            <span className="text-[5px] text-white/90 mt-0.5">www.asiabookofrecords.com</span>
          </span>
          <span className="w-px h-10 lg:h-12 bg-white/30" aria-hidden="true" />
          <span className="text-left">
            <span className="font-display font-extrabold text-base lg:text-xl leading-none block">
              8<sup className="text-[10px] lg:text-xs">th</sup>–14<sup className="text-[10px] lg:text-xs">th</sup>
            </span>
            <span className="font-display font-extrabold text-base lg:text-xl leading-none block">Aug, 2026</span>
          </span>
          <Button onClick={toggleFs} data-testid="fullscreen-toggle" variant="outline" className="hidden lg:inline-flex rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1F2B7B] px-5">
            {fs ? <><Minimize2 className="w-4 h-4 mr-1.5" /> Exit</> : <><Maximize2 className="w-4 h-4 mr-1.5" /> Fullscreen</>}
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 px-4 lg:px-6 py-3 grid gap-4 lg:grid-cols-[250px_minmax(0,1fr)_250px]">
        {/* Left: movement stats + share */}
        <aside className="hidden lg:flex flex-col gap-3 min-h-0 overflow-hidden order-2 lg:order-1">
          <div>
            <p className="rounded-t-xl bg-[#0056D2] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-1.5">The movement so far</p>
            <div className="space-y-2 rounded-b-xl border border-t-0 border-slate-200 bg-slate-50 p-2.5">
              <StatTile icon={Users} label={team ? "Team members pledged" : "Doctors participated"} value={stats?.total_doctors} />
              <StatTile icon={MapPin} label={team ? "HQs covered" : "Cities covered"} value={stats?.cities_count} />
              <StatTile icon={Landmark} label={team ? "Divisions" : "States & UTs"} value={stats?.states_count} />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3"><Share2 className="w-4 h-4" /> Share the movement</p>
            <div className="flex items-center gap-2">
              <a data-testid="share-whatsapp" className="flex-1 text-center text-xs font-semibold rounded-full bg-[#25D366]/10 text-[#128C7E] py-2 hover:bg-[#25D366]/20" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${shareText}%20${shareUrl}`}>WhatsApp</a>
              <a data-testid="share-x" className="flex-1 text-center text-xs font-semibold rounded-full bg-slate-900/5 text-slate-800 py-2 hover:bg-slate-900/10" target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}>X</a>
              <a data-testid="share-facebook" className="flex-1 text-center text-xs font-semibold rounded-full bg-[#1877F2]/10 text-[#1877F2] py-2 hover:bg-[#1877F2]/20" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}>Facebook</a>
            </div>
          </div>
        </aside>

        {/* Center: the map */}
        <main className="order-1 lg:order-2 min-w-0 min-h-0 flex flex-col">
          <div className="flex items-center justify-center gap-3 mb-1 shrink-0">
            <h2 className="font-display font-extrabold text-lg lg:text-xl tracking-[0.15em] uppercase text-slate-800">{team ? "Live Team Pledge Wall" : "Live Pledge Wall"}</h2>
            <span className="flex items-center gap-1.5 text-xs text-red-600 font-bold">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE
            </span>
          </div>
          {pledges.length === 0 && (
            <p className="text-center text-sm text-slate-400 mb-1 shrink-0">Waiting for the first pledges — the map fills as doctors pledge.</p>
          )}
          <div className="h-[60vh] lg:h-auto lg:flex-1 lg:min-h-0 relative">
            <IndiaMosaicMap pledges={mapPledges} onSelect={(p) => openPopup(p)} rawNames={team} />

            {/* Latest joiner stays spotlighted for ~35s (client request) */}
            <AnimatePresence>
              {spotlight && !incoming && (
                <motion.div
                  key={`spot-${spotlight.id}`}
                  data-testid="spotlight-card"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  className="absolute top-2 right-0 z-10"
                >
                  <button type="button" onClick={() => openPopup(spotlight)} className="text-left">
                    <div className="flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur border-2 border-[#138808] shadow-xl px-4 py-3 w-64">
                      <PledgeAvatar pledge={spotlight} size="w-12 h-12" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#138808]">Just joined 🎉</p>
                        <p className="font-display font-bold text-sm text-slate-900 truncate">
                          {team ? spotlight.name : displayDoctorName(spotlight.name)}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{joinMeta(spotlight.specialty, spotlight.city)}</p>
                      </div>
                    </div>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            {/* New pledge flying into the map */}
            <AnimatePresence>
              {incoming && (
                <motion.div
                  key={incoming.id}
                  data-testid="incoming-pledge-card"
                  initial={{ opacity: 0, y: 140, scale: 1.05 }}
                  animate={{ opacity: [0, 1, 1, 0.9, 0], y: [140, 0, 0, -40, -110], scale: [1.05, 1, 1, 0.55, 0.12] }}
                  transition={{ duration: 1.6, times: [0, 0.25, 0.6, 0.85, 1], ease: "easeInOut" }}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
                >
                  <div className="flex items-center gap-3 rounded-2xl bg-white border-2 border-[#FF9933] shadow-2xl px-5 py-4 w-72">
                    <PledgeAvatar pledge={incoming} size="w-14 h-14" text="text-base" />
                    <div className="min-w-0">
                      <p className="font-display font-extrabold text-slate-900 truncate">
                        {team ? incoming.name : displayDoctorName(incoming.name)}
                      </p>
                      <p className="text-xs text-[#0056D2] font-semibold truncate">{incoming.specialty}</p>
                      <p className="text-xs text-slate-500 truncate">{joinMeta(incoming.city, incoming.state)}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-center text-xs font-bold text-[#138808]">joined the wall! 🎉</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>

        {/* Right: search + recent pledges */}
        <aside className="flex flex-col gap-2 lg:min-h-0 overflow-hidden order-3">
          <div className="relative" data-testid="doctor-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="doctor-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a doctor…"
              className="pl-9 rounded-full bg-white"
            />
            {results !== null && (
              <div className="absolute z-30 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                {results.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">No doctor found</p>
                ) : (
                  results.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid="doctor-search-result"
                      onClick={() => { openPopup(p, { autoClose: true }); setQuery(""); }}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2 hover:bg-blue-50 transition"
                    >
                      <PledgeAvatar pledge={p} size="w-8 h-8" text="text-[10px]" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900 truncate">
                          {team ? p.name : displayDoctorName(p.name)}
                        </span>
                        <span className="block text-[11px] text-slate-500 truncate">{joinMeta(p.specialty, p.city)}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <p className="rounded-t-xl bg-[#138808] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-1.5 shrink-0">Recent pledges</p>
          <div className="lg:flex-1 lg:min-h-0 rounded-b-xl border border-t-0 border-slate-200 bg-slate-50 p-2.5 space-y-2 overflow-hidden">
            {pledges.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-6">No pledges yet</p>
            ) : (
              pledges.slice(0, 12).map((p) => <RecentPledgeRow key={p.id} pledge={p} onSelect={(x) => openPopup(x)} noDr={team} />)
            )}
          </div>
        </aside>
      </div>

      {/* Running brand-logo strip (images from public/Logos) */}
      <div data-testid="brand-strip" className="shrink-0 bg-white py-2 border-t border-slate-200 shadow-inner">
        <Marquee gradient={false} speed={40} autoFill>
          {BRAND_LOGOS.map((src) => (
            <img
              key={src}
              src={src}
              alt="Brand logo"
              className="h-9 w-auto mx-8 object-contain"
              // a deleted logo file simply disappears from the strip
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ))}
        </Marquee>
      </div>

      {/* Record finale: countdown then the achieved number */}
      <AnimatePresence>
        {(finaleStart != null || finalInfo) && (
          <motion.div
            key="finale"
            data-testid="finale-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/95 backdrop-blur-sm pointer-events-none"
          >
            {finalInfo ? (
              <div className="text-center px-6" data-testid="finale-final">
                <div className="tricolor-bar h-2 w-48 mx-auto rounded-full mb-8" />
                <p className="font-display font-extrabold text-2xl lg:text-4xl tracking-[0.3em] text-[#FF9933] uppercase">Record Achieved</p>
                <p className="font-display font-black text-white leading-none tracking-tight text-[16vw] lg:text-[13rem]">
                  {(finalInfo.count ?? 0).toLocaleString("en-IN")}
                </p>
                <p className="mt-2 font-display font-bold text-xl lg:text-3xl text-slate-200 uppercase tracking-[0.2em]">Pledges by the Doctors of India</p>
                <p className="mt-6 text-sm lg:text-lg text-slate-400">
                  {CAMPAIGN.title} · Asia Book of Records Attempt · {CAMPAIGN.dates}
                </p>
                <p className="mt-3 text-xs lg:text-sm text-slate-500">An initiative by {ORG.name}</p>
              </div>
            ) : (
              <div className="text-center px-6" data-testid="finale-countdown">
                <p className="font-display font-extrabold text-xl lg:text-3xl tracking-[0.3em] text-[#FF9933] uppercase">Completing the record</p>
                <motion.p
                  key={`n-${finaleNum}`}
                  initial={{ scale: 1.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 16 }}
                  className="font-display font-black text-white leading-none text-[38vw] lg:text-[24rem]"
                  data-testid="finale-number"
                >
                  {finaleNum}
                </motion.p>
                <p className="font-display font-extrabold text-2xl lg:text-5xl text-[#7CFC9B]">
                  <CountUp value={stats?.total_pledges ?? 0} /> <span className="text-slate-300 text-lg lg:text-3xl">pledges and counting…</span>
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Milestone banner (fireworks play behind it) */}
      <AnimatePresence>
        {milestone && (
          <motion.div
            key={`ms-${milestone.value}`}
            data-testid="milestone-banner"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 200, damping: 18 }}
            className="fixed inset-0 z-50 grid place-items-center pointer-events-none"
          >
            <div className="rounded-[2.5rem] bg-slate-950/90 backdrop-blur border-4 border-[#FF9933] shadow-2xl px-12 py-10 text-center">
              <div className="tricolor-bar h-1.5 w-32 mx-auto rounded-full mb-6" />
              <p className="font-display font-black text-white text-6xl lg:text-8xl leading-none tracking-tight">
                🎉 {milestone.value.toLocaleString("en-IN")}
              </p>
              <p className="mt-3 font-display font-extrabold text-2xl lg:text-4xl tracking-[0.2em] text-[#FF9933] uppercase">
                {milestone.label}
              </p>
              <p className="mt-4 text-sm lg:text-base text-slate-300">…and counting. Thank you, doctors of India!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Doctor record popup */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && closePopup()}>
        <DialogContent data-testid="pledge-record-dialog" className="max-w-sm rounded-3xl bg-white p-0 overflow-hidden">
          {selected && (
            <div>
              <div className="tricolor-bar h-1.5 w-full" />
              <div className="p-6 text-center">
                <div className="flex justify-center">
                  <PledgeAvatar pledge={selected} size="w-24 h-24" text="text-2xl" />
                </div>
                <DialogTitle className="mt-4 font-display font-extrabold text-2xl text-slate-900">
                  {team ? selected.name : displayDoctorName(selected.name)}
                </DialogTitle>
                <p className="mt-1 text-sm font-semibold text-[#0056D2]">{selected.specialty}</p>
                <p className="text-sm text-slate-500">{joinMeta(selected.city, selected.state)}</p>
                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-left">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Pledged on</p>
                  <p className="text-sm font-semibold text-slate-800">{formatIST(selected.created_at)}</p>
                </div>
                <p className="mt-4 text-xs italic text-slate-500 leading-relaxed">
                  “{selected.pledge_text || "I pledge to dedicate myself to the service of humanity and a healthier India."}”
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-widest font-bold text-[#138808]">#HealthyIndiaPledge</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
